import { useState, useEffect } from 'react';
import { Root, Portal, Overlay, Content, Close, Title, Description } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import close from '@/assets/icons/close.svg';
import { CreateInquiryDtoOut } from '../../models/out/CreateInquiryDtoOut';
import { InquiryResourceField } from '../../models/common/InquiryResourceField';
import InquiryResourceSummary from './InquiryResourceSummary';
import Button from '../ui/Button';
import styles from './NewInquiryDialog.module.scss';

interface NewInquiryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (dto: CreateInquiryDtoOut) => void;
  resourceType?: string | null;
  resourceId?: string | null;
  /** Snapshot of the row this was opened from: shown here, and stored on the inquiry. */
  resourceSummary?: InquiryResourceField[];
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

export default function NewInquiryDialog({
  open, onClose, onSubmit, resourceType, resourceId, resourceSummary, isSubmitting, errorMessage,
}: NewInquiryDialogProps) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');

  const resetForm = () => {
    setSubject('');
    setContent('');
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const handleSubmit = () => {
    if (!subject.trim() || !content.trim()) return;
    onSubmit({
      subject: subject.trim(),
      content: content.trim(),
      resourceType: resourceType || null,
      resourceId: resourceId || null,
      resourceSummary,
      attachmentDocumentIds: [],
    });
  };

  return (
    <Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <Portal>
        <Overlay className={styles.overlay} />
        <Content className={styles.content}>
          <Close className={styles.closeBtn} aria-label={t('inquiries.newInquiryDialog.cancel')}>
            <img src={close} alt="" width={20} height={20} />
          </Close>
          <div className={styles.body}>
            <Title asChild>
              <h2 className={`heading-h4-semibold ${styles.title}`}>{t('inquiries.newInquiryDialog.title')}</h2>
            </Title>
            <Description className="sr-only">{t('inquiries.newInquiryDialog.content')}</Description>
            {/*
              * The whole row, not a chip with an id. A coordinator asking about a transaction
              * should see which one they are asking about before they write, and the office
              * gets the same block on the other side without opening another tab.
              */}
            <InquiryResourceSummary
              resourceType={resourceType ?? null}
              resourceId={resourceId ?? null}
              fields={resourceSummary}
            />
            <div className={styles.field}>
              <label className={styles.label} htmlFor="inquiry-subject">{t('inquiries.newInquiryDialog.subject')}</label>
              <input
                id="inquiry-subject"
                className={styles.input}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('inquiries.newInquiryDialog.subjectPlaceholder')}
                autoFocus
                required
              />
            </div>
            {errorMessage && (
              <div className={styles.errorBox} role="alert">{errorMessage}</div>
            )}
            <div className={styles.field}>
              <label className={styles.label} htmlFor="inquiry-content">{t('inquiries.newInquiryDialog.content')}</label>
              <textarea
                id="inquiry-content"
                className={`${styles.input} ${styles.textarea}`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('inquiries.newInquiryDialog.contentPlaceholder')}
                required
                rows={5}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button variant="white" type="button" onClick={onClose} disabled={isSubmitting}>
              {t('inquiries.newInquiryDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmit}
              disabled={!subject.trim() || !content.trim() || isSubmitting}
            >
              {t('inquiries.newInquiryDialog.submit')}
            </Button>
          </div>
        </Content>
      </Portal>
    </Root>
  );
}
